import { useSettings } from '../stores/settings';

/**
 * Motor de audio: un AudioContext compartido con buses de música y efectos.
 * Todo el sonido se sintetiza (sin archivos), así que no hay licencias de por medio.
 */
class AudioEngine {
  ctx: AudioContext | null = null;
  master!: GainNode;
  music!: GainNode;
  sfx!: GainNode;
  private noiseBuf: AudioBuffer | null = null;
  private unlocked = false;

  /** Crea el contexto en el primer gesto del usuario (política de autoplay). */
  init(): void {
    if (this.ctx || typeof window === 'undefined') return;
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.music = this.ctx.createGain();
    this.sfx = this.ctx.createGain();
    const comp = this.ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.ratio.value = 3;
    this.music.connect(this.master);
    this.sfx.connect(this.master);
    this.master.connect(comp).connect(this.ctx.destination);
    this.applyVolumes();
    useSettings.subscribe(() => this.applyVolumes());
  }

  unlock(): void {
    this.init();
    if (!this.ctx || this.unlocked) return;
    void this.ctx.resume().then(() => (this.unlocked = true));
  }

  get ready(): boolean {
    return !!this.ctx && this.ctx.state === 'running';
  }

  applyVolumes(): void {
    if (!this.ctx) return;
    const s = useSettings.getState();
    const t = this.ctx.currentTime;
    this.master.gain.setTargetAtTime(s.muted ? 0 : s.master, t, 0.05);
    this.music.gain.setTargetAtTime(s.music * 0.55, t, 0.2);
    this.sfx.gain.setTargetAtTime(s.sfx, t, 0.05);
  }

  noise(): AudioBuffer {
    if (this.noiseBuf) return this.noiseBuf;
    const ctx = this.ctx!;
    const buf = ctx.createBuffer(1, ctx.sampleRate * 1.5, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    this.noiseBuf = buf;
    return buf;
  }
}

export const audio = new AudioEngine();

if (typeof window !== 'undefined') {
  const unlock = () => audio.unlock();
  window.addEventListener('pointerdown', unlock, { passive: true });
  window.addEventListener('keydown', unlock);
}
